import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateQuestPopup = ({ isOpen, onClose, initialContext, onQuestCreated }) => {
  const [context, setContext] = useState(initialContext || "");
  const [title, setTitle] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
    });

  const handleSubmit = async () => {
    const tags = tagsInput
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag !== "");

    const creatorName = localStorage.getItem("userEmail")?.split("@")[0];
    if (!creatorName) {
      alert("User not logged in.");
      return;
    }

    let base64Image = null;
    if (imageFile) {
      base64Image = await convertToBase64(imageFile);
    }

    const payload = {
      title,
      context,
      tags,
      creatorName,
      image: base64Image,
    };

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/quests/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        console.log("✅ Quest created:", data);
        if (onQuestCreated) onQuestCreated();
        onClose();
      } else {
        console.error("❌ Failed to create quest:", data.message);
        alert(data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("❌ Server error:", err);
      alert("An error occurred while submitting your quest.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl p-6 overflow-y-auto max-h-[90vh]"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold font-['Rubik'] text-slate-800 mb-4">
              Create a New Quest
            </h2>

            <div className="space-y-4">
              {/* Title */}
              <div className="text-left">
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Give your quest a short title"
                />
              </div>

              {/* Context */}
              <div className="text-left">
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Context
                </label>
                <textarea
                  rows={3}
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* Tags */}
              <div className="text-left">
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="e.g. growth, frontend, design"
                />
              </div>

              {/* Image Upload */}
              <div className="text-left">
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Optional Image (e.g. screenshot or diagram)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="text-sm text-slate-700"
                />
                {imagePreview && (
                  <div className="mt-2 relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-auto rounded-lg border"
                    />
                    <button
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-1 right-1 bg-white text-red-500 border border-red-500 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold hover:bg-red-100"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 text-sm rounded-md bg-cyan-600 text-white hover:bg-cyan-700 transition disabled:opacity-50"
              >
                {loading ? "Posting..." : "Post Quest"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateQuestPopup;
